import type {
  ICanBrowseSkillMarketplaceProps,
  ICanPublishSkillToMarketplaceProps,
  IFilterSkillMarketplaceListingsProps,
  IIsSkillMarketplaceActionOfferedProps,
  IPublishSkillMarketplaceDraft,
  IPublishSkillMarketplacePayload,
  ISkillMarketplaceListing,
} from "../types/skill-marketplace";
import { ESkillMarketplaceScope } from "../types/skill-marketplace";

// NOTE: NSAI-7391 — the skills marketplace rules both clients gate on. Kept here
// so web and mobile cannot drift. Write is accepted on its own because the
// permission payload the client receives is not implies-expanded, while the
// server grants read to every write holder.
export const canBrowseSkillMarketplace = ({
  isPersonalProject,
  hasMarketplaceSkillReadPermission,
  hasMarketplaceSkillWritePermission,
}: ICanBrowseSkillMarketplaceProps): boolean =>
  !isPersonalProject &&
  (hasMarketplaceSkillReadPermission || hasMarketplaceSkillWritePermission);

// NOTE: Mirrors the server's publisher rule so "Publish to marketplace" is only
// offered when the request would succeed.
export const canPublishSkillToMarketplace = ({
  isPersonalProject,
  hasMarketplaceSkillWritePermission,
  isSkillOwner,
  isSkillPublic,
  hasPublicSkillWritePermission,
  isCatalogManagedCopy,
}: ICanPublishSkillToMarketplaceProps): boolean =>
  !isPersonalProject &&
  hasMarketplaceSkillWritePermission &&
  !isCatalogManagedCopy &&
  // NOTE: Ownership counts for a private skill only — a public skill needs
  // public_skill:write whoever created it, as on the server.
  (isSkillPublic ? hasPublicSkillWritePermission : isSkillOwner);

// NOTE: Whether the publish / manage entry is worth showing for a skill the
// caller may publish. A listed skill stays manageable whatever its state, so a
// publisher can always take its listing down; a new listing needs a skill the
// server would accept — enabled and not archived.
export const isSkillMarketplaceActionOffered = ({
  isListed,
  isSkillEnabled,
  isSkillArchived,
}: IIsSkillMarketplaceActionOfferedProps): boolean =>
  isListed || (isSkillEnabled && !isSkillArchived);

// NOTE: The skills list reads a skill's published state from the marketplace
// list: a listing whose skill uuid matches. The map makes it an O(1) lookup.
export const buildSkillMarketplaceListingMap = (
  listings: ISkillMarketplaceListing[] | null | undefined,
): Map<string, ISkillMarketplaceListing> => {
  const listingBySkillUuid = new Map<string, ISkillMarketplaceListing>();

  for (const listing of listings ?? []) {
    listingBySkillUuid.set(listing.skill.uuid, listing);
  }

  return listingBySkillUuid;
};

export const buildPublishSkillMarketplaceDraft = (
  listing: ISkillMarketplaceListing | null,
): IPublishSkillMarketplaceDraft => ({
  accessScope: listing?.accessScope ?? ESkillMarketplaceScope.ORGANIZATION,
  isDefault: listing?.isDefault ?? false,
  targetProjectUuids: listing?.targetProjectUuids ?? [],
});

// NOTE: The server refuses `isDefault` outside the organization scope and a
// selected-projects scope without targets, so each scope sends only its own
// fields.
export const buildPublishSkillMarketplacePayload = ({
  accessScope,
  isDefault,
  targetProjectUuids,
}: IPublishSkillMarketplaceDraft): IPublishSkillMarketplacePayload =>
  accessScope === ESkillMarketplaceScope.ORGANIZATION
    ? { accessScope, isDefault }
    : // NOTE: isDefault goes out as false, not absent: the server keeps the
      // stored flag for an absent field, so narrowing a default listing would
      // otherwise be refused as a default listing with selected projects.
      { accessScope, isDefault: false, targetProjectUuids };

export const isPublishSkillMarketplaceDraftSubmittable = ({
  accessScope,
  targetProjectUuids,
}: IPublishSkillMarketplaceDraft): boolean =>
  accessScope === ESkillMarketplaceScope.ORGANIZATION ||
  targetProjectUuids.length > 0;

// NOTE: Client-side search over the one list the page already holds: name,
// description and the publishing project.
export const filterSkillMarketplaceListings = ({
  listings,
  search,
}: IFilterSkillMarketplaceListingsProps): ISkillMarketplaceListing[] => {
  const needle = search.trim().toLowerCase();

  if (needle.length === 0) return listings ?? [];

  return (listings ?? []).filter((listing) =>
    [
      listing.skill.name,
      listing.skill.description,
      listing.sourceProjectTitle ?? "",
    ].some((value) => value.toLowerCase().includes(needle)),
  );
};
